import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/Button/Button";
import {
  clearVacationFeedback,
  createVacation,
  deleteVacation,
  fetchVacationBalance,
  fetchVacations,
  updateVacation,
} from "../../redux/vacationSlice";
import "./VacationsSection.css";

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "-";
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const parseIsoDateOnly = (dateValue) => {
  const match = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getTotalDays = (startDateValue, endDateValue) => {
  const start = parseIsoDateOnly(startDateValue);
  const end = parseIsoDateOnly(endDateValue);

  if (!start || !end) return 0;

  const dayMs = 24 * 60 * 60 * 1000;
  const diff = Math.floor((end - start) / dayMs) + 1;
  return diff > 0 ? diff : 0;
};

// Días hábiles (lunes a viernes) entre dos fechas, ambas incluidas: así descuenta la API.
const getWeekdays = (startDateValue, endDateValue) => {
  const toDate = (value) => (value instanceof Date ? value : parseIsoDateOnly(value));
  const start = toDate(startDateValue);
  const end = toDate(endDateValue);

  if (!start || !end || start > end) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

// Días descontados por una vacación. Las antiguas no tienen `days` y se contaban en calendario.
const getChargedDays = (item, startDateValue, endDateValue) => {
  if (item?.isBirthdayHalfDay) return 0;
  if (typeof item?.days === "number") return item.days;
  return getTotalDays(startDateValue, endDateValue);
};

const getDaysInYear = (startDateValue, endDateValue, year, weekdaysOnly = false) => {
  const start = parseIsoDateOnly(startDateValue);
  const end = parseIsoDateOnly(endDateValue);

  if (!start || !end) {
    return 0;
  }

  const rangeStart = new Date(year, 0, 1);
  const rangeEnd = new Date(year, 11, 31);

  const effectiveStart = start > rangeStart ? start : rangeStart;
  const effectiveEnd = end < rangeEnd ? end : rangeEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  if (weekdaysOnly) {
    return getWeekdays(effectiveStart, effectiveEnd);
  }

  const dayMs = 24 * 60 * 60 * 1000;
  return Math.floor((effectiveEnd - effectiveStart) / dayMs) + 1;
};

const toDateInputValue = (dateValue) => {
  if (!dateValue) return "";
  // Extract YYYY-MM-DD directly from ISO string to avoid timezone shift
  const match = String(dateValue).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

export default function VacationsSection() {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isBirthdayHalfDay, setIsBirthdayHalfDay] = useState(false);
  const [localValidationError, setLocalValidationError] = useState("");
  const { employee } = useSelector((state) => state.auth);

  const {
    vacations,
    balance,
    errorDeleteVacation,
    statusFetchVacations,
    errorFetchVacations,
    statusCreateVacation,
    errorCreateVacation,
    messageCreateVacation,
    statusUpdateVacation,
    errorUpdateVacation,
    messageUpdateVacation,
  } = useSelector((state) => state.vacation);

  useEffect(() => {
    dispatch(fetchVacations());
    dispatch(fetchVacationBalance());
  }, [dispatch]);

  useEffect(() => {
    if (!isModalOpen) {
      dispatch(clearVacationFeedback());
    }
  }, [dispatch, isModalOpen]);

  const rows = useMemo(() => {
    return (Array.isArray(vacations) ? vacations : []).map((item, index) => {
      const fechaInicio = item?.dateRange?.startDate || item?.startDate;
      const fechaFinal = item?.dateRange?.endDate || item?.endDate;

      return {
        rowId: item?._id || `vacation-${index}`,
        id: index + 1,
        fechaInicio,
        fechaFinal,
        diasTotales: getChargedDays(item, fechaInicio, fechaFinal),
        isBirthdayHalfDay: Boolean(item?.isBirthdayHalfDay),
        countsWeekdays: typeof item?.days === "number",
      };
    });
  }, [vacations]);

  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setIsBirthdayHalfDay(false);
    setEditingItem(null);
    setLocalValidationError("");
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingItem(row);
    setStartDate(toDateInputValue(row.fechaInicio));
    setEndDate(toDateInputValue(row.fechaFinal));
    setIsBirthdayHalfDay(Boolean(row.isBirthdayHalfDay));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalValidationError("");

    if (!startDate || !endDate) {
      return;
    }

    if (!editingItem) {
      if (!hasUnlockedVacations) {
        setLocalValidationError("No tienes vacaciones desbloqueadas para solicitar.");
        return;
      }

      const requestedDays = isBirthdayHalfDay ? 0 : getWeekdays(startDate, endDate);
      if (requestedDays > availableVacationDays) {
        setLocalValidationError(
          `Solo puedes solicitar hasta ${availableVacationDays} día(s) disponibles.`
        );
        return;
      }
    }

    try {
      if (editingItem) {
        await dispatch(
          updateVacation({
            id: editingItem.rowId,
            startDate,
            endDate,
          })
        ).unwrap();
      } else {
        await dispatch(
          createVacation({
            startDate,
            endDate,
            isBirthdayHalfDay,
          })
        ).unwrap();
      }

      dispatch(fetchVacationBalance());
      closeModal();
    } catch {
      // Feedback handled from redux state.
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("¿Eliminar estas vacaciones? Los días vuelven a tu saldo.")) return;

    try {
      await dispatch(deleteVacation(row.rowId)).unwrap();
      dispatch(fetchVacationBalance());
    } catch {
      // Feedback handled from redux state.
    }
  };

  const isSaving =
    statusCreateVacation === "loading" || statusUpdateVacation === "loading";
  const selectedTotalDays = getWeekdays(startDate, endDate);
  const hasCompleteDateRange = Boolean(startDate && endDate);
  const hasVacationConfig = Boolean(balance);
  const currentYear = new Date().getFullYear();
  const accumulatedDays = Number(balance?.accumulatedDays ?? 0);
  const bonusDays = Number(balance?.bonusDays ?? 0);
  const usedDaysFromDatabase = Number(balance?.usedDays ?? 0);
  const totalUnlockedDays = accumulatedDays + bonusDays;
  const takenVacationDaysInCurrentYear = useMemo(() => {
    return rows.reduce((total, row) => {
      const fechaInicio = row?.fechaInicio;
      const fechaFinal = row?.fechaFinal;
      if (!fechaInicio || !fechaFinal) return total;

      if (row?.isBirthdayHalfDay) {
        const parsedStart = parseIsoDateOnly(fechaInicio);
        if (!parsedStart) return total;

        const startYear = parsedStart.getFullYear();
        return startYear === currentYear ? total + 0.5 : total;
      }

      return total + getDaysInYear(fechaInicio, fechaFinal, currentYear, row?.countsWeekdays);
    }, 0);
  }, [currentYear, rows]);
  const availableVacationDays = useMemo(() => {
    const total = Number(balance?.availableDays ?? 0);
    return total > 0 ? total : 0;
  }, [balance]);
  const hasUnlockedVacations = availableVacationDays > 0;
  const requestedVacationDays = isBirthdayHalfDay ? 0 : selectedTotalDays;
  const exceedsAvailableDays =
    !editingItem && hasCompleteDateRange && requestedVacationDays > availableVacationDays;

  const birthdayMonthDay = useMemo(() => {
    if (!employee?.birthdayDate) return null;
    const match = String(employee.birthdayDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return `${match[2]}-${match[3]}`;
  }, [employee]);

  const isSingleDayVacation = hasCompleteDateRange && startDate === endDate;
  const selectedYear = hasCompleteDateRange ? startDate.slice(0, 4) : null;
  const isBirthdayVacationDate =
    Boolean(birthdayMonthDay) &&
    isSingleDayVacation &&
    startDate.slice(5, 10) === birthdayMonthDay;
  const birthdayHalfDayAlreadyTakenInSelectedYear = useMemo(() => {
    if (!selectedYear) return false;

    return (Array.isArray(vacations) ? vacations : []).some((item) => {
      if (!item?.isBirthdayHalfDay) return false;
      if (editingItem?.rowId && item?._id === editingItem.rowId) return false;

      const itemStartDate = item?.dateRange?.startDate || item?.startDate;
      if (!itemStartDate) return false;

      const parsedDate = parseIsoDateOnly(itemStartDate);
      if (!parsedDate) return false;

      return String(parsedDate.getFullYear()) === selectedYear;
    });
  }, [editingItem, selectedYear, vacations]);
  const canUseBirthdayHalfDay = Boolean(birthdayMonthDay) && isBirthdayVacationDate;
  const shouldShowBirthdayHalfDayOption =
    canUseBirthdayHalfDay && !birthdayHalfDayAlreadyTakenInSelectedYear;

  useEffect(() => {
    if (!shouldShowBirthdayHalfDayOption && isBirthdayHalfDay) {
      setIsBirthdayHalfDay(false);
    }
  }, [shouldShowBirthdayHalfDayOption, isBirthdayHalfDay]);

  const selectedTotalDaysLabel = !hasCompleteDateRange
    ? "-"
    : isBirthdayHalfDay
      ? "Medio día"
      : selectedTotalDays;

  return (
    <section className="profile-submenu-content" role="tabpanel" aria-label="Vacaciones">
      <div className="vacaciones-header">
        <h2>Vacaciones</h2>
        <Button
          type="button"
          variant="outline"
          size="small"
          className="vacaciones-create-btn"
          onClick={openCreateModal}
          disabled={!hasUnlockedVacations}
          title={!hasUnlockedVacations ? "No tienes vacaciones desbloqueadas" : "Tomar vacaciones"}
        >
          Tomar vacaciones
        </Button>
      </div>

      {errorFetchVacations && (
        <p className="vacaciones-feedback error">{errorFetchVacations}</p>
      )}
      {errorDeleteVacation && (
        <p className="vacaciones-feedback error">{errorDeleteVacation}</p>
      )}

      <div className="vacaciones-config-card">
        {hasVacationConfig && (
          <div className="vacaciones-config-item">
            <span className="vacaciones-config-label">Días acumulados + extras</span>
            <span className="vacaciones-config-value">{totalUnlockedDays}</span>
            <span className="vacaciones-config-subvalue">
              Acumulados: {accumulatedDays} | Extras: {bonusDays}
            </span>
          </div>
        )}
        {hasVacationConfig && (
          <div className="vacaciones-config-item">
            <span className="vacaciones-config-label">Días tomados total (BD)</span>
            <span className="vacaciones-config-value">{usedDaysFromDatabase}</span>
          </div>
        )}
        <div className="vacaciones-config-item">
          <span className="vacaciones-config-label">Días tomados en {currentYear}</span>
          <span className="vacaciones-config-value">{takenVacationDaysInCurrentYear}</span>
        </div>

        {hasVacationConfig && (
          <div
            className={`vacaciones-config-item vacaciones-config-item--available${!hasUnlockedVacations ? " vacaciones-config-item--alert" : ""}`}
          >
            <span className="vacaciones-config-label">Días disponibles</span>
            <span className={`vacaciones-config-value vacaciones-config-available${!hasUnlockedVacations ? " is-alert" : ""}`}>
              {availableVacationDays}
            </span>
            {!hasUnlockedVacations && (
              <span className="vacaciones-config-alert">No tiene vacaciones disponibles</span>
            )}
          </div>
        )}
      </div>

      <div className="vacaciones-table-wrap">
        <table className="vacaciones-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Fecha de inicio</th>
              <th>Fecha final</th>
              <th>Dias totales</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {statusFetchVacations === "loading" ? (
              <tr>
                <td colSpan="5" className="vacaciones-empty">
                  Cargando vacaciones...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="5" className="vacaciones-empty">
                  No hay vacaciones registradas.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                // Mismas reglas que la API: se editan mientras no hayan terminado
                // y se eliminan solo antes de empezar.
                const startParsed = row.fechaInicio ? parseIsoDateOnly(row.fechaInicio) : null;
                const endParsed = row.fechaFinal ? parseIsoDateOnly(row.fechaFinal) : null;
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                const canEdit = Boolean(endParsed) && endParsed.getTime() >= todayStart.getTime();
                const canDelete = Boolean(startParsed) && startParsed.getTime() > todayStart.getTime();
                const lockedEditTitle = "Solo se pueden editar vacaciones que no han terminado";

                return (
                  <tr key={row.rowId}>
                    <td>{row.id}</td>
                    <td>{formatDate(row.fechaInicio)}</td>
                    <td>{formatDate(row.fechaFinal)}</td>
                    <td>{row.isBirthdayHalfDay ? "Medio día" : row.diasTotales}</td>
                    <td>
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="onlyText"
                          size="small"
                          className="vacaciones-edit-btn"
                          onClick={() => openEditModal(row)}
                        >
                          Editar
                        </Button>
                      ) : (
                        <span className="vacaciones-edit-locked" title={lockedEditTitle}>—</span>
                      )}
                      {canDelete && (
                        <Button
                          type="button"
                          variant="onlyText"
                          size="small"
                          className="vacaciones-edit-btn"
                          onClick={() => handleDelete(row)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="vacaciones-modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="vacaciones-modal"
            role="dialog"
            aria-modal="true"
            aria-label={editingItem ? "Editar vacaciones" : "Tomar vacaciones"}
            onClick={(event) => event.stopPropagation()}
          >
            <h3>{editingItem ? "Editar vacaciones" : "Tomar vacaciones"}</h3>

            <form className="vacaciones-form" onSubmit={handleSubmit}>
              <label htmlFor="vacation-start-date">Fecha de inicio</label>
              <input
                id="vacation-start-date"
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  setLocalValidationError("");
                }}
                required
              />

              <label htmlFor="vacation-end-date">Fecha final</label>
              <input
                id="vacation-end-date"
                type="date"
                value={endDate}
                onChange={(event) => {
                  setEndDate(event.target.value);
                  setLocalValidationError("");
                }}
                required
              />

              <p className="vacaciones-total-days" aria-live="polite">
                Días hábiles: {selectedTotalDaysLabel}
              </p>

              {shouldShowBirthdayHalfDayOption && (
                <label className="vacaciones-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isBirthdayHalfDay}
                    onChange={(event) => setIsBirthdayHalfDay(event.target.checked)}
                  />
                  Medio dia de cumpleaños
                </label>
              )}

              {!birthdayMonthDay && (
                <p className="vacaciones-feedback note">
                  Este empleado no tiene birthdayDate configurado.
                </p>
              )}

              {birthdayHalfDayAlreadyTakenInSelectedYear && isBirthdayVacationDate && (
                <p className="vacaciones-feedback note">
                  El medio dia de cumpleaños ya fue solicitado en este año.
                </p>
              )}

              {exceedsAvailableDays && (
                <p className="vacaciones-feedback note">
                  Los dias solicitados superan los dias disponibles ({availableVacationDays}).
                </p>
              )}

              {localValidationError && (
                <p className="vacaciones-feedback error">{localValidationError}</p>
              )}

              {(errorCreateVacation || errorUpdateVacation) && (
                <p className="vacaciones-feedback error">
                  {errorCreateVacation || errorUpdateVacation}
                </p>
              )}
              {(messageCreateVacation || messageUpdateVacation) && (
                <p className="vacaciones-feedback success">
                  {messageCreateVacation || messageUpdateVacation}
                </p>
              )}

              <div className="vacaciones-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="vacaciones-form-btn"
                  onClick={closeModal}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="outline"
                  size="small"
                  className="vacaciones-form-btn"
                  disabled={
                    isSaving ||
                    !hasCompleteDateRange ||
                    selectedTotalDays <= 0 ||
                    exceedsAvailableDays ||
                    (!editingItem && !hasUnlockedVacations)
                  }
                >
                  {isSaving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}